import { useState, useEffect } from "react";
import { NavLink } from "./NavLink";
import { Button } from "./ui/button";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "@/assets/logo-nav.png";

type NavItem = {
  to?: string;
  label: string;
  children?: { to: string; label: string; desc?: string }[];
};

const navItems: NavItem[] = [
  {
    label: "Ontdekken",
    children: [
      { to: "/lob-reis", label: "De LOB-reis", desc: "Stap voor stap van ontdekken naar kiezen" },
      { to: "/voor-leerlingen", label: "Voor leerlingen", desc: "Ontdek wie je bent en wat bij je past" },
    ],
  },
  {
    label: "Ontwikkelen",
    children: [
      { to: "/lob-leerlijn", label: "LOB-leerlijn", desc: "Een samenhangend programma voor elk schoolniveau" },
      { to: "/leerlingprofiel", label: "Leerlingprofiel", desc: "Een persoonlijk profiel dat blijft groeien" },
      { to: "/hoe-werkt-het", label: "Doorlopend LOB-dossier", desc: "Ontwikkeling, ervaringen en keuzes op één plek" },
    ],
  },
  {
    label: "Begeleiden",
    children: [
      { to: "/voor-mentoren", label: "Voor mentoren & decanen", desc: "Inzicht, voortgang en betere gesprekken" },
      { to: "/dashboard", label: "Klas- en schooloverzicht", desc: "Zie voortgang en waar aandacht nodig is" },
    ],
  },
  {
    label: "Kiezen",
    children: [
      { to: "/keuzehulp", label: "Keuzehulp", desc: "Technologie als ondersteuning, de leerling beslist" },
      { to: "/opleidingen", label: "Opleidingen ontdekken", desc: "Vergelijken, open dagen en vervolgstappen" },
    ],
  },
  {
    label: "Voor scholen",
    children: [
      { to: "/voor-scholen", label: "Voor scholen", desc: "Meer samenhang in LOB, meer tijd voor de leerling" },
      { to: "/onderbouwing", label: "Onderbouwing & privacy", desc: "Transparant over methodiek, data en grenzen" },
      { to: "/privacy-en-veiligheid", label: "Privacy & veiligheid", desc: "Zorgvuldig omgaan met leerlinggegevens" },
      { to: "/resultaten", label: "Resultaten", desc: "Wat studie connect oplevert" },
      { to: "/integratie", label: "Implementatie", desc: "Soepel invoeren op school" },
      { to: "/prijzen", label: "Prijzen", desc: "Transparante tarieven" },
    ],
  },
  { to: "/blog", label: "Kennisbank" },
  { to: "/over-ons", label: "Over ons" },
];

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openDrop, setOpenDrop] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMenuOpen) return;

    const scrollY = window.scrollY;
    const body = document.body;
    const previous = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";

    const desktop = window.matchMedia("(min-width: 1280px)");
    const closeOnDesktop = () => {
      if (desktop.matches) setIsMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMenuOpen(false);
    };
    desktop.addEventListener("change", closeOnDesktop);
    window.addEventListener("keydown", closeOnEscape);

    return () => {
      Object.assign(body.style, previous);
      window.scrollTo({ top: scrollY, behavior: "instant" });
      desktop.removeEventListener("change", closeOnDesktop);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [isMenuOpen]);

  const solid = scrolled || isMenuOpen;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 pt-3">
      <div
        className={`container mx-auto max-w-[1340px] transition-all duration-300 rounded-2xl ${
          solid
            ? "bg-background lg:bg-background/80 backdrop-blur-xl border border-border shadow-large"
            : "bg-card lg:bg-card/60 backdrop-blur-md border border-border/60 shadow-soft"
        }`}
      >
        <div className="px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          <NavLink to="/" className="flex shrink-0 items-center">
            <img src={logo} alt="studie connect" width={156} height={36} {...{ fetchpriority: "high" }} decoding="async" className="h-9 w-auto" />
          </NavLink>


          {/* desktop nav */}
          <div className="hidden xl:flex items-center gap-1">
            {navItems.map((item) =>
              item.children ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setOpenDrop(item.label)}
                  onMouseLeave={() => setOpenDrop(null)}
                >
                  <button
                    className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-3 py-2 rounded-md hover:bg-muted"
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${
                        openDrop === item.label ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {openDrop === item.label && (
                    <div className="absolute left-0 top-full pt-2 w-80 animate-fade-in">
                      <div className="bg-card border border-border rounded-2xl shadow-large p-2">
                        {item.children.map((c) => (
                          <button
                            key={c.to}
                            onClick={() => {
                              navigate(c.to);
                              setOpenDrop(null);
                            }}
                            className="w-full text-left px-3 py-3 rounded-xl hover:bg-muted transition-colors"
                          >
                            <div className="text-sm font-bold text-secondary">
                              {c.label}
                            </div>
                            {c.desc && (
                              <div className="text-xs text-muted-foreground mt-0.5">
                                {c.desc}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to!}
                  className="whitespace-nowrap text-muted-foreground hover:text-foreground transition-colors text-sm font-medium px-3 py-2 rounded-md hover:bg-muted"
                  activeClassName="text-primary bg-accent"
                >
                  {item.label}
                </NavLink>
              )
            )}
          </div>

          <div className="hidden xl:flex shrink-0 items-center gap-4 border-l border-border pl-5">
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              <a href="https://portal.studie-connect.nl" target="_blank" rel="noopener noreferrer">
                Login
              </a>
            </Button>
            <Button
              size="sm"
              onClick={() => navigate("/demo")}
              className="bg-primary text-primary-foreground hover:bg-primary-hover font-semibold text-sm gap-1.5 rounded-full"
            >
              Kennismaken
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>

          <Button
            variant="ghost"
            size="icon"
            className="xl:hidden h-10 w-10 text-foreground"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </Button>
        </div>

        {isMenuOpen && (
          <div
            id="mobile-navigation"
            className="xl:hidden max-h-[calc(100dvh-6rem-env(safe-area-inset-bottom))] overflow-y-auto overscroll-contain py-4 border-t border-border animate-fade-in"
          >
            <div className="flex flex-col gap-1">
              {navItems.map((item) =>
                item.children ? (
                  <div key={item.label} className="py-1">
                    <div className="px-3 py-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                      {item.label}
                    </div>
                    {item.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className="block text-secondary hover:text-primary transition-colors py-2.5 px-5 rounded-md hover:bg-muted text-sm font-medium"
                        activeClassName="text-primary bg-accent"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to!}
                    className="text-secondary hover:text-primary transition-colors py-2.5 px-3 rounded-md hover:bg-muted text-sm font-medium"
                    activeClassName="text-primary bg-accent"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.label}
                  </NavLink>
                )
              )}
              <div className="flex flex-col gap-2 pt-4 border-t border-border mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  asChild
                  className="w-full justify-start text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                >
                  <a href="https://portal.studie-connect.nl" target="_blank" rel="noopener noreferrer" onClick={() => setIsMenuOpen(false)}>
                    Login
                  </a>
                </Button>
                <Button
                  size="sm"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary-hover font-semibold text-sm rounded-full"
                  onClick={() => { navigate("/demo"); setIsMenuOpen(false); }}
                >
                  Plan een kennismaking
                  <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </div>
    </nav>

  );
};

export default Navigation;
